import { useState } from "react";
import {
  Alert, Box, Chip, CircularProgress, Link, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  fetchApprovalQueue, type ApprovalQueueRow, type ApprovalQueueSort, type ApprovalQueueStatus,
} from "@/api/approvalQueues";
import { errorMessage } from "@/api/closeReview";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usd = (n: number | null) => (n == null ? "" : USD.format(n));
const day = (d: string | null) => (d ? dayjs(d).format("MMM D, YYYY") : "");

// Approved is open-ended, so it shows the last 30 days of bill dates unless the
// date filter says otherwise.
export const APPROVED_DEFAULT_DAYS = 30;
const MAX_PER_PAGE = 100;

// The Pending approval / Rejected / Approved tabs on the Triage page: vendor
// bills read live from NetSuite, with what the app knows laid over them.
// Read-only; each bill opens in NetSuite.
export function ApprovalQueueTable({ status, filters, onPage, onPerPage }: {
  status: ApprovalQueueStatus;
  filters: { q?: string; vendor_id?: string; date_from?: string; date_to?: string; page: number; per_page: number };
  onPage: (page: number) => void;
  onPerPage: (perPage: number) => void;
}) {
  const dateFrom = filters.date_from
    ?? (status === "approved" ? dayjs().subtract(APPROVED_DEFAULT_DAYS, "day").format("YYYY-MM-DD") : undefined);
  // The endpoint caps a page at 100 (the email queues allow 200).
  const perPage = Math.min(filters.per_page, MAX_PER_PAGE);
  // Column sort, as on the other tables: a new column starts descending, the
  // same column flips. Unset means the tab's own order.
  const [sort, setSort] = useState<{ key: ApprovalQueueSort; dir: "asc" | "desc" } | null>(null);
  const toggleSort = (key: ApprovalQueueSort) => {
    setSort((prev) => ({ key, dir: prev?.key === key && prev.dir === "desc" ? "asc" : "desc" }));
    onPage(1);
  };
  const params = {
    status, page: filters.page, per_page: perPage, q: filters.q, vendor_id: filters.vendor_id,
    date_from: dateFrom, date_to: filters.date_to, sort: sort?.key, dir: sort?.dir,
  };
  const header = (label: string, key?: ApprovalQueueSort, align?: "right") => (
    <TableCell align={align}>
      {key ? (
        <TableSortLabel active={sort?.key === key} direction={sort?.key === key ? sort.dir : "asc"} onClick={() => toggleSort(key)}>
          {label}
        </TableSortLabel>
      ) : label}
    </TableCell>
  );
  const query = useQuery({
    queryKey: ["approval-queue", params],
    queryFn: () => fetchApprovalQueue(params),
    placeholderData: (prev) => prev,
  });

  if (query.isError) return <Alert severity="error">{errorMessage(query.error)}</Alert>;
  if (!query.data) return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;

  const { items, total } = query.data;
  return (
    <>
      {status === "approved" && !filters.date_from && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Bills dated in the last {APPROVED_DEFAULT_DAYS} days. Use the date filter to look further back.
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {header("Vendor", "vendor")}
              {header("Invoice", "invoice")}
              {header("Amount", "amount", "right")}
              {header("Bill date", "bill_date")}
              {header("Approver", "approver")}
              {/* Who decided is only known for decisions made in the app, so it can't be sorted in NetSuite. */}
              {status === "pending" ? header("Waiting since", "created") : header("Decided")}
              {header("Entered by", "entered_by")}
              {header(status === "pending" ? "Status" : "Comment")}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.netsuite_bill_id} hover>
                <TableCell>
                  {r.netsuite_url
                    ? <Link href={r.netsuite_url} target="_blank" rel="noopener">{r.vendor_name}</Link>
                    : r.vendor_name}
                </TableCell>
                <TableCell>{r.tranid}</TableCell>
                <TableCell align="right">{usd(r.amount)}</TableCell>
                <TableCell>{day(r.trandate)}</TableCell>
                <TableCell>{r.approver_name ?? <em>None</em>}</TableCell>
                <TableCell>{status === "pending" ? day(r.created) : decided(r)}</TableCell>
                <TableCell>{r.submitter_name}</TableCell>
                <TableCell>{status === "pending" ? <PendingStatus r={r} /> : r.comment}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8}><em>Nothing matches these filters.</em></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <TablePagination
        component="div"
        count={total}
        page={filters.page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, p) => onPage(p + 1)}
        onRowsPerPageChange={(e) => onPerPage(parseInt(e.target.value, 10))}
      />
    </>
  );
}

// Who decided, when the app knows it (decisions made in Charted only show the outcome).
function decided(r: ApprovalQueueRow): string {
  if (!r.decided_by_name) return "In Charted";
  return `${r.decided_by_name}${r.decided_at ? `, ${dayjs(r.decided_at).format("MMM D")}` : ""}`;
}

function PendingStatus({ r }: { r: ApprovalQueueRow }) {
  if (r.reroute) {
    return (
      <Tooltip title={r.reroute.comment ?? ""}>
        <Chip size="small" color="warning" variant="outlined" label={`Sent back: should go to ${r.reroute.to_name}`} />
      </Tooltip>
    );
  }
  if (r.emailed_at) return <Typography variant="body2">Emailed {dayjs(r.emailed_at).format("MMM D")}</Typography>;
  return <Typography variant="body2" color="text.secondary">In Charted</Typography>;
}
